import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {PollsService} from '../../_services/polls.service';
import {take} from 'rxjs';
import {NestedOption, Poll, PollOption, PollQuestion} from '../../_types/polls';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Community, UserAccessLevel} from "../../_types/communities";
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";

@Component({
    selector: 'app-poll-view[pollId], app-poll-view[pollData]',
    templateUrl: './poll-view.component.html',
    styleUrl: './poll-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('barWidthAnimation', [
            state('*', style({width: '{{width}}'}), {params: {width: '0%'}}),
            transition('* => *', [
                animate('2000ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            ])
        ])
    ]
})
export class PollViewComponent implements OnChanges {
    @Input() pollData!: Poll;
    @Input() small: boolean = false;
    @Input() preview: boolean = false;
    @Input() forceShowResults: boolean = false;
    @Output() updatePoll: EventEmitter<Poll> = new EventEmitter<Poll>();
    community: Community | null = null
    showResult: boolean = false;
    voteSending: boolean = false;
    steps!: number;

    constructor(
        private readonly pollsService: PollsService,
        private readonly cdr: ChangeDetectorRef,
        private readonly contentService: ContentService,
        private readonly snack: SnackbarService,
    ) {
        this.contentService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
    }

    @Input() set pollId(value: number) {
        if (!value) return;
        this.pollsService.getPollById(value).pipe(take(1)).subscribe((res): void => {
            this.updateData(res);
        });
    };

    get hasTimePassed(): boolean {
        if (!this.pollData?.end_at) return false
        const now = new Date();
        const targetDate = new Date(this.pollData?.end_at);
        return targetDate < now;
    }

    get pollVoteValid(): boolean {
        return this.pollData.questions.every(question => this.questionPollVoteValid(question));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pollData']?.currentValue) {
            this.updateData(changes['pollData']?.currentValue);
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    vote(): void {
        this.voteSending = true;
        let options: any[] = []
        for (let question of this.pollData.questions) {
            if (question.results?.length && question.results?.length > 0) {
                options = options.concat(question.results)
            }
            for (let option of question.options) {
                if (option.nestedResults?.length && option.nestedResults?.length > 0) {
                    options = options.concat(option.nestedResults)
                }
            }
        }

        this.pollsService.votePoll(this.pollData.id, options)
            .pipe(
                take(1)
            )
            .subscribe((res): void => {
                this.voteSending = false;
                this.pollData = res.data;
                this.showResult = true;
                this.updatePoll.emit(res.data);
                this.cdr.detectChanges();
            }, e => {
                this.voteSending = false;
                this.cdr.detectChanges();
                this.snack.openErrorSnackBar(e)
            })
    }

    getPercentage(votes: number): string {
        return ((votes / (this.pollData.total_votes || 1)) * 100).toFixed(1) + '%';
    }

    setResult(item: any, array: string, option: PollOption | NestedOption, isMultiple: boolean): void {
        let updatedValue: number[] = [];
        // if current option is not 'N/a' then loop through question and remove values for 'na'
        if (isMultiple) {
            if (option.not_applicable) {
                item[array] = [option.id];
            } else {
                if (item[array]?.includes(option.id)) {
                    updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                    item[array] = updatedValue;
                } else {
                    updatedValue = [...(item[array] ?? []), option.id];
                    item[array] = updatedValue;
                }
                // if current option is not 'N/a' then loop through question and remove values for 'na'
                for (let question of this.pollData.questions) {
                    for (let option of question.options) {
                        if (option.not_applicable && item[array].includes(option.id)) {
                            updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                            item[array] = updatedValue;
                        }
                        for (let nestedOption of option.nested_options) {
                            if (nestedOption.not_applicable && item[array].includes(nestedOption.id)) {
                                updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                                item[array] = updatedValue;
                            }
                        }
                    }
                }
            }
        } else {
            if (this.pollData.matrix_exclusive) {
                //     If matrix exclusive. then get index of option and loop through all questions and unselect any other question option with the same index
                const selectedIndex: number = item.options.findIndex((x: any): boolean => x.id === option.id);

                for (let question of this.pollData.questions) {
                    // as matrix_exclusive and !isMultiple then there will only ever be one result in question.results
                    if (question.results && Array.isArray(question.results) && question.results[0]) {
                        // @ts-ignore
                        let questionSelectionIndex: number = question.options.findIndex((x: any): boolean => x.id === question.results[0]);
                        if (questionSelectionIndex == selectedIndex) {
                            // this option has been selected elsewhere so remove.
                            question.results = [];
                        }
                    }
                }
            }
            item[array] = [option.id];
        }

        this.pollData.questions.forEach(question => {
            const questionOptions = question.options.map(opt => opt.id);
            if (!this.displayConditionsMet(question)) {
                question['results'] = (question['results'] || []).filter(res => !questionOptions.includes(res));
            }
        });

        // Uncheck/change parent will clear checked nested options
        if (array === 'results') {
            item.options.filter((opt: PollOption) => !item[array]?.includes(opt.id) && opt.nested_options)
                .forEach((opt: PollOption) => {
                    opt['nestedResults'] = [];
                })
        }
    }

    updateData(poll: Poll): void {
        this.showResult = this.hasTimePassed || this.forceShowResults || !!poll?.user_vote?.length || (!this.hasCommunityRole(['member']) && !this.hasCommunityRole(['full_admin']));
        if (!this.showResult) {
            this.pollData = this.orderPoll(poll);
        } else {
            this.pollData = poll;
        }
        this.cdr.detectChanges();
    }

    displayConditionsMet(question: PollQuestion): boolean {
        let numberOfConditions: number = question.display_condition_options.length
        let metConditionsNumber: number = 0;
        let conditionsMet: boolean = true;
        if (question.display_condition_options.length > 0 && !this.showResult) {
            for (let condtion of question.display_condition_options) {
                for (let question of this.pollData.questions) {
                    if (question.results?.includes(condtion)) {
                        metConditionsNumber++
                    }
                    for (let option of question.options) {
                        if (option.nestedResults?.includes(condtion)) {
                            metConditionsNumber++
                        }
                    }
                }
            }
            // tally for each time a condition is met. once the same amount fo conditions have been met that exist then mark as met
            conditionsMet = metConditionsNumber === numberOfConditions;
        } else {
            conditionsMet = true;
        }
        return conditionsMet
    }

    changeVote(): void {
        this.orderPoll(this.pollData);
        this.showResult = false
    }

    orderPoll(poll: Poll): Poll {
        if (poll.shuffle_questions) {
            this.randomize(poll.questions);
        } else if (poll.questions) {
            poll?.questions?.sort((a, b) => a.position - b.position);
        }

        // Randomize options if boolean is set
        for (let question of poll.questions) {
            if (question.randomize_options) {
                // Only randomize options that aren't fixed
                question.options = this.randomize(question.options);
            }
            for (let option of question.options) {
                if (option.nested_options && option.nested_options.length > 0 && option.randomize_nested_options) {
                    // Only randomize options that aren't fixed
                    option.nested_options = this.randomize(option.nested_options);
                }
            }
        }
        return poll
    }

    shuffleArray(array: any) {
        for (let i: number = array.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    randomize(arr: any) {
        // Step 1: Find the indices of objects that have fixed false
        let matchingIndices = arr.reduce((acc: any, obj: any, index: number) => {
            if (!obj['fixed']) {
                acc.push(index);
            }
            return acc;
        }, []);

        // Step 2: Extract matching objects based on their indices
        let matchingObjects = matchingIndices.map((index: number) => arr[index]);

        // Step 3: Shuffle the extracted objects
        matchingObjects = this.shuffleArray(matchingObjects);

        // Step 4: Put the shuffled objects back in their original positions
        matchingIndices.forEach((index: number, i: number): void => {
            arr[index] = matchingObjects[i];
        });

        return arr;
    }

    public questionPollVoteValid(question: PollQuestion): boolean {
        const hasMatchingId = (options: PollOption[] | NestedOption[], results: number[]): boolean => {
            return options.some(option => results?.length ? results.includes(option.id) : false);
        };

        const checkNestedOptions = (option: PollOption): boolean => {
            if (option.nested_options?.length && question.results?.includes(option.id)) {
                if (!option.nestedResults) return false;
                if (!hasMatchingId(option.nested_options, option.nestedResults)) {
                    return false;
                }
                for (const nestedOption of option.nested_options) {
                    if (!checkNestedOptions(nestedOption as any)) {
                        return false;
                    }
                }
            }
            return true;
        };

        // If the question is displayed conditionally then it is valid if the conditions aren't met.
        if (question.display_condition_options.length > 0 && !this.displayConditionsMet(question)) {
            return true;
        }

        if (!hasMatchingId(question.options, question?.results as any)) {
            return false;
        }
        for (const option of question.options) {
            if (!checkNestedOptions(option)) {
                return false;
            }
        }
        return true;
    }

    getQuestion(selectedIndex: number) {
        return this.pollData.questions[selectedIndex];
    }

    updateSteps(steps: number) {
        this.steps = steps;
        this.cdr.detectChanges();
    }
}
